import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import api from "@/lib/api";
import Eyebrow from "@/components/common/Eyebrow";

const TILE_WIDTH = 200;

export default function DestinationsGrid() {
  const [destinations, setDestinations] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    api.get("/villas/destinations").then((r) => setDestinations(r.data)).catch(() => setDestinations([]));
  }, []);

  const scrollBy = (dir) => {
    scrollerRef.current?.scrollBy({ left: dir * (TILE_WIDTH + 16) * 2, behavior: "smooth" });
  };

  return (
    <Box component="section" id="destinations" sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, lg: 6 }, pt: { xs: 6, md: 8 } }} data-testid="destinations-section">
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Eyebrow sx={{ mb: 1 }}>Popular destinations</Eyebrow>
          <Typography component="h2" className="font-serif-display" sx={{ fontSize: { xs: 26, md: 32 } }}>Where will you escape?</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box component={Link} to="/villas" sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, fontWeight: 500, color: "text.primary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
            View all destinations <ArrowRight size={14} />
          </Box>
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.75 }}>
            <IconButton size="small" onClick={() => scrollBy(-1)} data-testid="destinations-prev" sx={{ border: 1, borderColor: "divider" }}><ChevronLeft size={16} /></IconButton>
            <IconButton size="small" onClick={() => scrollBy(1)} data-testid="destinations-next" sx={{ border: 1, borderColor: "divider" }}><ChevronRight size={16} /></IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        ref={scrollerRef}
        className="no-scrollbar"
        data-testid="destinations-carousel"
        sx={{ display: "flex", gap: { xs: 1.25, sm: 2 }, overflowX: "auto", scrollSnapType: "x mandatory", pb: 1 }}
      >
        {destinations === null
          ? [...Array(6)].map((_, i) => (
            <Box key={i} sx={{ flex: { xs: "0 0 96px", sm: `0 0 ${TILE_WIDTH}px` } }}>
              <Skeleton variant="rounded" sx={{ aspectRatio: { xs: "1 / 1", sm: "4 / 3" }, borderRadius: { xs: "50%", sm: 3 } }} />
            </Box>
          ))
          : destinations.map((d) => (
            <Box
              key={d.name}
              component={Link}
              to={`/villas?destination=${encodeURIComponent(d.name)}`}
              data-testid={`destination-tile-${d.name}`}
              sx={{
                flex: { xs: "0 0 96px", sm: `0 0 ${TILE_WIDTH}px` },
                textDecoration: "none", display: "block", scrollSnapAlign: "start",
              }}
            >
              <Box sx={{ position: "relative", aspectRatio: { xs: "1 / 1", sm: "4 / 3" }, borderRadius: { xs: "50%", sm: 3 }, overflow: "hidden", mb: { xs: 1, sm: 0 }, "&:hover img": { transform: "scale(1.06)" } }}>
                <Box component="img" src={d.image} alt={d.name} sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease" }} />
                <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)", display: { xs: "none", sm: "block" } }} />
                <Box sx={{ position: "absolute", left: 12, bottom: 12, right: 12, color: "#fff", display: { xs: "none", sm: "block" } }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{d.name}</Typography>
                  <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>{d.count}+ villas</Typography>
                </Box>
              </Box>
              <Box sx={{ display: { xs: "block", sm: "none" }, textAlign: "center" }}>
                <Typography sx={{ fontWeight: 600, fontSize: 12, color: "text.primary", lineHeight: 1.25 }}>{d.name}</Typography>
                <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{d.count}+ villas</Typography>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
